import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';
import { Util } from '@grandvisionhq/forms';
import { getCheckout, getMyAccount } from '../selectors.js';
import { isUninitialized } from '../state.js';
import { ShippingMethod } from '../models/step-shipping/shipping-methods.js';

const { getValuesFromState  } = Util;
/**
 * Get state for shipping step
 */ const getShipping = createSelector(getCheckout, (checkout)=>checkout && !isUninitialized(checkout) && checkout.shipping ? checkout.shipping : undefined
);
/**
 * Get user selections
 */ const getSelectedShippingMethod = createSelector(getShipping, (shipping)=>shipping && shipping.selectedShippingMethod
);
const getSelectedStore = createSelector(getShipping, (shipping)=>shipping && shipping.selectedStore ? shipping.selectedStore : undefined
);
const getServiceStore = createSelector(getShipping, (shipping)=>shipping && (shipping.serviceStore || shipping.serviceStore === null) ? shipping.serviceStore : undefined
);
const getSelectedPickupPoint = createSelector(getShipping, (shipping)=>shipping && shipping.selectedPickupPoint ? shipping.selectedPickupPoint : undefined
);
const getConsents = createSelector(getShipping, (shipping)=>shipping && shipping.consents ? shipping.consents : undefined
);
const isConsentGiven = createSelector(getConsents, (consents)=>consents && !consents.some((consent)=>consent.isChecked === false
    )
);
const getChangedOptIns = createSelector(getMyAccount, (myAccount)=>myAccount && myAccount.changedOptIns
);
const shouldValidateMyAccount = createSelector(getShipping, (shipping)=>shipping && shipping.shouldValidateMyAccount ? shipping.shouldValidateMyAccount : false
);
const shouldValidateServiceStore = createSelector(getShipping, (shipping)=>shipping && shipping.shouldValidateServiceStore ? shipping.shouldValidateServiceStore : false
);
/**
 * Get user's shipping details state
 */ const getShippingDetails = createSelector(getShipping, (shipping)=>shipping && shipping.shippingDetails
);
/**
 * These selectors either return the raw form values of the form data or undefined
 * If they return undefined it could also mean the user didn't toggle this part of the step in the checkout
 */ const dataOrUndefined = (form)=>form && form.isEnabled ? getValuesFromState(form.formData) : undefined
;
const isContactDetailsReady = createSelector(getShippingDetails, (details)=>details && details.contactDetails && !isEmpty(details.contactDetails.formData)
);
createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.contactDetails)
);
createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.expressCheckout)
);
const isBillingAndShippingAddressSame = createSelector(getShipping, (shipping)=>shipping && shipping.isBillingAndDeliveryAddressSame || false
);
const isDeliveryFormEnabled = createSelector(getShippingDetails, (details)=>details && details.deliveryAddress && details.deliveryAddress.isEnabled
);
const getBillingAddress = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.billingAddress)
);
const isBillingAddressReady = createSelector(getShippingDetails, (details)=>details && details.billingAddress && !isEmpty(details.billingAddress.formData)
);
const isDeliveryAddressReady = createSelector(getShippingDetails, (details)=>details && details.deliveryAddress && !isEmpty(details.deliveryAddress.formData)
);
const getDeliveryAddress = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.deliveryAddress)
);
const hasStoredDeliveryAddress = createSelector(getShippingDetails, (details)=>{
    var ref;
    const values = details && getValuesFromState(details === null || details === void 0 ? void 0 : (ref = details.deliveryAddress) === null || ref === void 0 ? void 0 : ref.formData);
    return !values ? false : Object.keys(values).some((key)=>values[key]
    );
});
const getCompany = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.company)
);
const getInvoice = createSelector(getShippingDetails, (details)=>details && dataOrUndefined(details.invoice)
);
/**
 * Get only the values from all currently enabled shipping forms
 */ const getShippingData = createSelector(getShippingDetails, (details)=>details && Object.entries(details).reduce((filtered, [name, form])=>{
        if (form.isEnabled) {
            filtered[name] = getValuesFromState(form.formData);
        }
        return filtered;
    }, {})
);
/**
 * Shipping step validity state
 */ const isShippingStepValid = createSelector(getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint, getShippingDetails, isConsentGiven, getConsents, (shippingMethod, store, pickupPoint, details, consentGiven, consents)=>{
    return !!details && Object.values(details).filter((form)=>form.isEnabled
    ).every((form)=>form.isValid
    ) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.Pickup ? store !== undefined : true) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.PickupPoint ? pickupPoint !== undefined : true) && (consents && consentGiven ? consentGiven : false) && !!(shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.isValid);
});
const isExpressCheckoutValid = createSelector(getSelectedShippingMethod, getSelectedStore, getSelectedPickupPoint, isConsentGiven, getShippingDetails, getConsents, (shippingMethod, store, pickupPoint, consentGiven, details, consents)=>{
    var ref, ref1;
    return !!details && // some opcos don't have additional express checkout fields. In that case, it's valid by default.
    (isEmpty(details === null || details === void 0 ? void 0 : (ref = details.expressCheckout) === null || ref === void 0 ? void 0 : ref.formData) || ((ref1 = details.expressCheckout) === null || ref1 === void 0 ? void 0 : ref1.isValid)) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.Pickup ? store !== undefined : true) && ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.method) === ShippingMethod.PickupPoint ? pickupPoint !== undefined : true) && (consents && consentGiven ? consentGiven : false) && !!(shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.isValid);
});
const isUserDataPrefilled = createSelector(getShipping, (shipping)=>{
    return (shipping === null || shipping === void 0 ? void 0 : shipping.isUserDataPrefilled) || false;
});
createSelector(getShipping, (shipping)=>{
    return (shipping === null || shipping === void 0 ? void 0 : shipping.isAddressBookDataPrefilled) || false;
});
createSelector(getShipping, (shipping)=>{
    return shipping === null || shipping === void 0 ? void 0 : shipping.addressBookPrefillValidationResult;
});

export { getBillingAddress, getChangedOptIns, getCompany, getConsents, getDeliveryAddress, getInvoice, getSelectedPickupPoint, getSelectedShippingMethod, getSelectedStore, getServiceStore, getShipping, getShippingData, getShippingDetails, hasStoredDeliveryAddress, isBillingAddressReady, isBillingAndShippingAddressSame, isConsentGiven, isContactDetailsReady, isDeliveryAddressReady, isDeliveryFormEnabled, isExpressCheckoutValid, isShippingStepValid, isUserDataPrefilled, shouldValidateMyAccount, shouldValidateServiceStore };
//# sourceMappingURL=selectors.js.map
